import { Space } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Configuration } from 'api/timesheets/configuration/configuration';
import { ReactComponent as GoogleCalenderIcon } from 'assets/svgs/google-calendar.svg';
import { ReactComponent as OutlookCalenderIcon } from 'assets/svgs/outlook.svg';
import { Radio } from 'components/atoms/radio';
import { RadioGroup } from 'components/atoms/radio-buttons';
import { Switch } from 'components/atoms/switch/switch';
import { BodyShort01 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import {
  useAccountTimesheetIntegrationProvidersMutation,
  useAccountTimesheetIntegrationProvidersQuery,
} from 'queries/timesheets/calendar-integration-provider';
import { ControlPanel } from '../control-panel';

const Control = styled.div`
  ${BodyShort01};
  display: flex;
  gap: var(--spacing-05);
  align-items: center;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  svg {
    width: 18px;
    height: 18px;
  }
`;

const StyledRadioGroup = styled(RadioGroup)`
  ${Radio} {
    color: var(--gray-80);
    display: inline-flex;
    align-items: center;
    min-height: 40px;
  }
`;

enum IntegrationTypes {
  None = 'none',
  Google = 'google',
  Outlook = 'outlook',
}
const getValue = (
  type?: Configuration['integrationMeta'],
): IntegrationTypes => {
  if (type == null) {
    return IntegrationTypes.None;
  }
  if (type.googleCalendarEnabled) {
    return IntegrationTypes.Google;
  }
  if (type.outlookCalendarEnabled) {
    return IntegrationTypes.Outlook;
  }

  return IntegrationTypes.None;
};
const getPayload = (
  type?: IntegrationTypes,
): Configuration['integrationMeta'] => {
  if (type === IntegrationTypes.Google) {
    return {
      googleCalendarEnabled: true,
      outlookCalendarEnabled: false,
    };
  }
  if (type === IntegrationTypes.Outlook) {
    return {
      googleCalendarEnabled: false,
      outlookCalendarEnabled: true,
    };
  }

  return {
    googleCalendarEnabled: false,
    outlookCalendarEnabled: false,
  };
};

export const GoogleCalendar: FC = () => {
  const label = useTranslation('settings.timesheets.controls.calender.label');
  const description = useTranslation(
    'settings.timesheets.controls.calender.description',
  );

  const { data: accountIntegrations, isLoading } =
    useAccountTimesheetIntegrationProvidersQuery();
  const { mutateAsync, isLoading: isUpdating } =
    useAccountTimesheetIntegrationProvidersMutation();

  const enabled =
    getValue(accountIntegrations?.integrationMeta) !== IntegrationTypes.None;

  return (
    <ControlPanel label={label} descriptions={[description]}>
      <Control data-cy="switch-wrapper">
        <Switch
          size="small"
          checked={enabled}
          onChange={(checked) => {
            mutateAsync({
              payload: {
                ...accountIntegrations!,
                integrationMeta: getPayload(
                  checked ? IntegrationTypes.Google : IntegrationTypes.None,
                ),
              },
            });
          }}
        />
        {enabled ? (
          <FormattedMessage id="enabled" />
        ) : (
          <FormattedMessage id="disabled" />
        )}
      </Control>
      <StyledRadioGroup
        value={getValue(accountIntegrations?.integrationMeta)}
        onChange={(e) => {
          mutateAsync({
            payload: {
              ...accountIntegrations!,
              integrationMeta: getPayload(e.target.value as IntegrationTypes),
            },
          });
        }}
        disabled={isLoading || isUpdating}
      >
        <Space direction="vertical">
          <Radio value={IntegrationTypes.Google}>
            <Wrapper>
              <GoogleCalenderIcon />
              <FormattedMessage
                id={`settings.timesheets.controls.${IntegrationTypes.Google}-calender.connector`}
              />
            </Wrapper>
          </Radio>
          <Radio value={IntegrationTypes.Outlook}>
            <Wrapper>
              <OutlookCalenderIcon />
              <FormattedMessage
                id={`settings.timesheets.controls.${IntegrationTypes.Outlook}-calender.connector`}
              />
            </Wrapper>
          </Radio>
        </Space>
      </StyledRadioGroup>
    </ControlPanel>
  );
};
